import { detect, BrowserInfo as DetectBrowser } from 'detect-browser'
import { getMediaTypes, MediaType } from '@/utils/media-types'

interface BrowserFeatures {
  /** Browser support for canvas captureStream */
  captureStream: boolean
  /** Browser can download a file created from URL.createObjectURL */
  downloadObjectURL: boolean;
  /** Browser support for Web Workers */
  webWorkers: boolean
  /** Browser support for MediaRecorder */
  mediaRecorder: boolean
  /** Browser is capabale of running ConvertToMp4 */
  convertToMp4: boolean
  /** MediaRecorder support for creating mp4 files */
  mp4Container: boolean
  /** MediaRecorder support for creating webm files with h264-encoded video streams */
  webmH264: boolean
  /** Javascript runtime support for SharedArrayBuffer */
  sharedArrayBuffer: boolean
  /** Browser supports web codecs */
  webCodecs: boolean
}

interface BrowserSupport {
  /** Browser supports Serato Visualizer */
  supported: boolean
  browser: DetectBrowser
  unsupportedOS: boolean
  /** Specific browser version has compatibility issues */
  blacklisted: boolean
  features: BrowserFeatures
  mediaTypes: MediaType[]
}

/**
 * Visualizer Browser Support
 *
 * Determines whether or not a web browser is capable of running
 * the Serato Visualizer.
 *
 * Current general requirements to run Serato Visualizer are:
 *
 * - Browser must support the Canvas `captureStream` method.
 * - Browser must be able to handle a file download created by URL.createObjectURL.
 * - Browser must support Web Workers.
 * - Browser must support MediaRecorder.
 * - MediaRecorder must capable of recording in the mp4 container format.
 *   - or -
 *   Browser must be capable of running ConvertToMp4.
 *   In browser feature terms specifically this means:
 *   - MediaRecorder must capable of recording in the webm container format
 *     with video encoded using the h264 or h265 codec, and
 *   - The javascript runtime must support SharedArrayBuffer (which is a
 *     requirement of ffmpeg WASM used by ConvertToMp4).
 *
 * Additionally, a blacklist identifies specific os/browser/version combinations
 * that have bugs or issues that prevent the use of Serato Visualizer.
 */
type GetBrowserSupport = () => BrowserSupport

const getBrowserSupport: GetBrowserSupport = (): BrowserSupport => {
  let mediaTypes: MediaType[] = []
  const browser = <DetectBrowser>detect()
  const unsupportedOS = browser.os === 'iOS' || browser.os === 'Android OS'
  const blacklisted = getBrowserBlacklisted(browser)
  const features = getBrowserFeatures(browser)

  if (features.mediaRecorder && (features.mp4Container || features.webmH264)) {
    mediaTypes = getMediaTypes()
  }

  return {
    supported: !blacklisted && getSeratoVisualizerSupported(features),
    browser,
    unsupportedOS,
    blacklisted,
    features,
    mediaTypes
  }
}

export {
  DetectBrowser,
  BrowserFeatures,
  BrowserSupport,
  GetBrowserSupport,
  getBrowserSupport
}

/**
 * Returns the overall ability of the browser to run Serato Visualizer
 */
function getSeratoVisualizerSupported (features: BrowserFeatures): boolean {
  // This feature is an overall requirement right now because the only way
  // to download a file is via URL.createObjectURL.
  // But in the future we may be able to workaround browsers that don't support
  // this by uploading the file to a web server and downloading it via an
  // HTTP request.
  return  features.downloadObjectURL &&
          features.webWorkers &&
          features.convertToMp4 &&
          features.webCodecs
}

/**
 * Returns a BrowserFeatures instance for the browser
 */
function getBrowserFeatures (browser: DetectBrowser): BrowserFeatures {
  const mediaTypes = getMediaTypes()
  const webmH264 = webmH264Support(browser, mediaTypes)
  const sab = sharedArrayBufferSupported()
  const webC = webCodecsSupported()

  return {
    captureStream: canvasCaptureStreamSupported(),
    downloadObjectURL: getBrowserCanDownloadObjectURL(browser),
    // Do we need to dig into specific Web Worker features???
    webWorkers: !!window.Worker,
    mediaRecorder: !!window.MediaRecorder,
    convertToMp4: (webmH264 && sab),
    mp4Container: mp4ContainersSupport(mediaTypes),
    webmH264: webmH264,
    sharedArrayBuffer: sab,
    webCodecs: webC
  }
}

/**
 * Determines whether or not the browser natively supports mp4 container
 * creation using MediaRecorder
 */
function mp4ContainersSupport (mediaTypes: MediaType[]): boolean {
  for (let i = 1; i < mediaTypes.length; i++) {
    if (mediaTypes[i].fileExtension.toLowerCase() === 'mp4') {
      return true
    }
  }
  return false
}

/**
 * Determines whether or not the browser natively supports webm container
 * creation and video streams encoded with h264 using MediaRecorder
 */
function webmH264Support (browser: DetectBrowser, mediaTypes: MediaType[]): boolean {
  // No versions of Firefox support h264 video codecs. But, on a Mac at least,
  // Firefox incorrectly reports that it does when using MediaRecorder.isTypeSupported.
  // So we can't rely on the values contained in `mediaTypes` and instead need to do
  // some plain old browser detection.
  if (browser.name.toLowerCase() === 'firefox') {
    return false
  }
  for (let i = 1; i < mediaTypes.length; i++) {
    // We only have to test for the container type and not the codec because
    // `mediaTypes` is only populated with container/codec combinations that
    // include the h264 codec.
    if (mediaTypes[i].fileExtension.toLowerCase() === 'webm') {
      return true
    }
  }
  return false
}

/**
 * Determines whether or not the Javascript runtime supports SharedArrayBuffer
 */
function sharedArrayBufferSupported (): boolean {
  try {
    new SharedArrayBuffer(1024)
    return true
  } catch {
    return false
  }
}

/**
 * Determines whether or not the browser supports Canvas `captureStream`
 */
function canvasCaptureStreamSupported (): boolean {
  const ele = document.createElement('canvas')
  return !!(ele.captureStream)
}

function getBrowserBlacklisted (browser: DetectBrowser): boolean {
  const blacklisted: DetectBrowser[] = [
    // Canvas captureStream is broken in macOS Safari 15.0. Worked in previous versions (>= 14.3 I think).
    // https://bugs.webkit.org/show_bug.cgi?id=229611
    { name: 'safari', os: 'Mac OS', type: 'browser', version: '15.0.0' }
  ]
  for (let i = 0; i < blacklisted.length; i++) {
    if (
      browser.name.toLowerCase() === blacklisted[i].name.toLowerCase() &&
      browser.os?.toLowerCase() === blacklisted[i].os?.toLowerCase() &&
      browser.type.toLowerCase() === blacklisted[i].type.toLowerCase() &&
      (browser.version.toLowerCase() === blacklisted[i].version.toLowerCase() || blacklisted[i].version === '*')
    ) {
      return true
    }
  }
  return false
}

/**
 * Determines whether or not a browser allows for a file download via a DOMString
 * created by URL.createObjectURL.
 *
 * We can test this as a specific browser feature, but only to a point.
 *
 * In practice some browsers (currently Chrome on iOS) just don't work properly
 * despite supporting the API.
 */
function getBrowserCanDownloadObjectURL (browser: DetectBrowser): boolean {
  if (window.URL && browser.name !== 'crios') {
    return true
  }
  return false
}

/**
 * Determines whether or not a browser supports webCodecs
 */
function webCodecsSupported () : boolean {
  try {
    new VideoEncoder({
      output: () => {},
      error: () => {}
    })
    return true
  } catch  {
    return false
  }
}
